// React imports
import { useState, useCallback, useEffect, useMemo } from 'react';

// Generic imports
import dayjs from 'dayjs';
import axios from 'axios';
import _ from 'lodash';
import pluralize from 'pluralize';

// MUI import
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import { red } from '@mui/material/colors';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHatWizard, faTimes, faXmark } from '@fortawesome/pro-light-svg-icons';

// Local imports
import StyledDropzone from '../components/StyledDropzone.js';
import { createAllRateProducts } from '../utils/product.js';
import { getRatesFromServer } from '../utils/rates.js';
import { RatesMain } from './Rates.js';
import { convertMonadaCsv } from './utils.js';

function CreateImport(props) {
    const { form, typeTitle, onNext, onBack } = props;
    const [ errors, setErrors ] = useState([]);
    const [ validFrom, setValidFrom] = useState(form?.validFrom ? dayjs(form.validFrom) : dayjs());
    const [ validUntil, setValidUntil] = useState(form?.validUntil ? dayjs(form.validUntil) : dayjs());
    const [ supplier, setSupplier] = useState(form?.supplier || '');

    function _onNext() {
        const errors = [];
        if (!_.trim(supplier)) errors.push('supplier');
        if (validFrom.valueOf() >= validUntil.valueOf()) errors.push('dates');
        setErrors(errors);
        if (errors.length > 0) return;

        const form = {
            supplier,
            validFrom: validFrom.format('YYYY-MM-DD'),
            validUntil: validUntil.format('YYYY-MM-DD')
        }

        onNext({ form });
    }

    return (
        <Card sx={{ ...style, width: '560px' }}>
            <CardContent>
                <Typography gutterBottom variant='h5' component='div'>
                    Get your rates from <b>{typeTitle}</b>
                </Typography>
                <TextField
                    label='Supplier (organization)'
                    value={supplier}
                    onChange={(e) => {
                        setSupplier(e.target.value);
                    }}
                    fullWidth
                    error={errors.includes('supplier')}
                    sx={{ mb: 2 }}
                />
                <MobileDatePicker
                    label='Valid From (DD/MM/YYYY)'
                    inputFormat='DD/MM/YYYY'
                    value={validFrom}
                    onChange={(newValue) => setValidFrom(newValue)}
                    renderInput={(params) => <TextField fullWidth {...params} sx={{ ...(params.sx || {}), mb: 2 }} error={errors.includes('dates')} />}
                />
                <MobileDatePicker
                    label='Valid Until (DD/MM/YYYY)'
                    inputFormat='DD/MM/YYYY'
                    value={validUntil}
                    onChange={(newValue) => setValidUntil(newValue)}
                    renderInput={(params) => <TextField fullWidth {...params} sx={{ ...(params.sx || {}), mb: 2 }} error={errors.includes('dates')} />}
                />
            </CardContent>
            <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button size='small' color='secondary' onClick={onBack}>
                    Back
                </Button>
                <Button size='small' color='primary' variant='contained' onClick={_onNext}>
                    Continue to upload file
                </Button>
            </CardActions>
        </Card>
    );
}

async function apiCreateRequest({ form, file, fileName, org, accesstoken, transportationMethod, shipmentType }) {
    const formData = new FormData();
    const blob = await (await fetch(file)).blob();
    formData.append('file', blob, fileName);
    formData.append('supplier', form.supplier);
    formData.append('validFrom', form.validFrom);
    formData.append('validUntil', form.validUntil);
    // Recorded so the import is identifiable later without opening the file — the
    // admin Rate Imports list and the parsing agent both key off it.
    if (transportationMethod) formData.append('transportationMethod', transportationMethod);
    if (shipmentType) formData.append('shipmentType', shipmentType);
    await axios.post(`/api/rates/import/monada?organizationId=${org.id}&accesstoken=${accesstoken}`, formData);
}

function PublishedRatesView(props) {
    const { ratesImport } = props;
    const [ forceRelease, setForceRelease ] = useState(false);
    if (!ratesImport.monadaRateCsv || forceRelease) {
        return <PublishedRatesViewPreFile {...props} />
    } else {
        return <PublishedRatesViewPostFile {...props} setForceRelease={setForceRelease} />
    }
}

function PublishedRatesViewPreFile(props) {
    const { me, org, handleClose, onUpdateRatesImport, accesstoken, ratesImport } = props;

    const [ errors, setErrors ] = useState([]);
    const [ uploading, setUploading ] = useState(false);
    const [ file, setFile ] = useState(null);

    const onDrop = useCallback(acceptedFiles => {
        acceptedFiles.forEach((_file) => {
            const reader = new FileReader();

            reader.onabort = () => console.log('file reading was aborted');
            reader.onerror = () => console.log('file reading has failed');
            reader.onload = () => {
                // Do whatever you want with the file contents
                const dataurl = reader.result
                console.log(dataurl)
                setFile({ file: _file, dataurl });
            };
            reader.readAsDataURL(_file);
        });
    }, [])

    function _handleClose() {
        setFile(null);
        handleClose();
    }

    async function onUploadSummary() {
        try {
            setUploading(true);
            const form = new FormData();
            const blob = await (await fetch(file.dataurl)).blob();
            form.append('file', blob, file.file.originalname || file.file.name);

            const { data: updatedImport } = await axios.post(`/api/rates/import/${ratesImport.id}/monada-rate-csv?organizationId=${org.id}&accesstoken=${accesstoken}`, form);
            onUpdateRatesImport({ ratesImport: updatedImport });
            setUploading(false);
            _handleClose();
        } catch (e) {
            setErrors(['upload']);
            setUploading(false);
        }
    }

    return (
        <Modal open={true} onClose={_handleClose} >
            <Card sx={{ ...style, width: '560px'}}>
                <CardContent>
                    <Typography variant='h6'>
                        Import Request
                    </Typography>
                    <Typography variant='caption' sx={{ opacity: 0.5 }}>
                        Supplier <b>{(ratesImport || {}).supplier}</b> | Valid <b>{(ratesImport || {}).validFrom} - {(ratesImport || {}).validUntil}</b> | File <b>{_.last(((ratesImport || {}).file || '').split('.'))}</b> (<a href={(ratesImport || {}).file} target={'_blank'} rel='noopener noreferrer'>Download</a>)
                    </Typography>
                    <hr style={{ opacity: 0.2 }} />
                    <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', textAlign: 'center', paddingTop: '12px' }}>
                        <FontAwesomeIcon icon={faHatWizard} style={{ fontSize: '48px', paddingBottom: '12px' }} />
                        <Typography variant='body1' sx={{ opacity: 1 }}>Our magical wizard will transform your files to Monada rates shortly</Typography>
                    </div>
                    {me.email === 'admin@monada.ai' && (
                        <div style={{ padding: '12px 18px', marginTop: '24px', borderRadius: '12px', boxShadow: '0px 0px 4px rgba(0,0,0,0.7)', background: red[100] }}>
                            <Typography variant='h6'>Admin Area</Typography>
                            <Typography variant='caption'>Download the file attached from the title of this modal, convert it to Monada CSV format, and upload the CSV here.</Typography>
                            {ratesImport.monadaRateCsv && (
                                <Typography variant='caption' component='div' sx={{ margin: '12px 0px' }}>
                                    <a href={ratesImport.monadaRateCsv} target={'_blank'} rel='noopener noreferrer'>Last uploaded CSV.</a>
                                </Typography>
                            )}
                            {!file && (<StyledDropzone onDrop={onDrop} accept={['.csv']} />)}
                            {file && (
                                <div
                                    style={{
                                        padding: '20px', borderWidth: 2, borderRadius: 2, borderColor: '#eeeeee',
                                        borderStyle: 'dashed', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center'
                                    }}
                                >
                                    <img alt='title excel' style={{ height: '64px', width: '64px', flex: 0 }} src='https://icons.iconarchive.com/icons/graphicloads/filetype/256/excel-xls-icon.png' />
                                    <Typography variant='caption' sx={{ flex: 1, paddingLeft: '12px' }}>
                                        {file.file.name}
                                    </Typography>
                                    <Button variant='text' color='secondary' onClick={() => setFile(null)}><FontAwesomeIcon icon={faTimes} /></Button>
                                </div>
                            )}
                            {errors.includes('upload') && (
                                <Typography variant='caption' sx={{ color: red[500] }}>
                                    Error uploading file
                                </Typography>
                            )}
                            <Button
                                fullWidth
                                variant='contained'
                                color='secondary'
                                sx={{ marginTop: '12px' }}
                                onClick={onUploadSummary}
                                disabled={uploading || !file?.dataurl}
                            >
                                {!uploading && <span>Done, return ball to customer</span>}
                                {uploading && <CircularProgress size={18} />}
                            </Button>
                        </div>
                    )}
                </CardContent>
                <CardActions style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}></div>
                    <Button variant='contained' size='small' color='primary' onClick={_handleClose}>
                        I'll come back later
                    </Button>
                </CardActions>
            </Card>
        </Modal>
    )
}

function PublishedRatesViewPostFile(props) {
    const { rates, handleClose, accesstoken, org: organization, ratesImport, onUpdateRatesImport, unlocode, seaPorts, setForceRelease } = props;
    const [ transformedRates, setTransformedRates ] = useState(null);
    const [ loading, setLoading ] = useState(null);
    const [ message, setMessage ] = useState(null);
    const [ forceSaving, setForceSaving ] = useState([]);
    const [ forceSavingSuccess, setForceSavingSuccess ] = useState([]);
    const [ forceSavingError, setForceSavingError ] = useState([]);
    const [ existingRates, setExistingRates ] = useState(null);
    const [ parsedRates, setParsedRates ] = useState(null);
    const [ failedParsedRates, setFailedParsedRates ] = useState([]);

    const products = useMemo(() => {
        const pairs = ['air', 'sea', 'land'].map(transportationMethod => ([transportationMethod, createAllRateProducts({ transportationMethod })]));
        return _.fromPairs(pairs);
    }, []);

    async function resetExisting() {
        (async () => {
            setTransformedRates(null);
            setParsedRates(null);
            setFailedParsedRates([]);
            setExistingRates(null);
            setLoading('Retreiving all rates...');
            const existingRates = (await getRatesFromServer({
                mode: 'rates', accesstoken, organizationId: organization.id, onProgress: ({ count, total }) => {
                    if (total > 0) {
                        setLoading(`Retreiving existing rates (${Math.min(Math.round(count / total * 100), 100)}%)...`);
                    }
                }
            })).filter(r => !r.deleted);
            setLoading(null);
            setExistingRates(existingRates);
        })();
    }

    function onDeleteOffer({ offerId }) {
        resetExisting();
    }

    useEffect(() => {
        resetExisting();
    }, [rates]);

    useEffect(() => {
        (async () => {
            const { data: csvData } = await axios.get(ratesImport.monadaRateCsv);
            const transformed = await convertMonadaCsv({ data: csvData, org: organization, unlocode, seaPorts });
            setFailedParsedRates(transformed.failed);
            setParsedRates(await Promise.all(transformed.successful.map(async (r) => ({ 
                ...r.rate, 
                attributes: {
                    source: ratesImport.file, 
                    importUUID: await deterministicUUID(ratesImport.monadaRateCsv, r.index)
                },
                id: await deterministicUUID(ratesImport.monadaRateCsv, r.index)
            }))));
        })();
    }, [ratesImport.monadaRateCsv]);

    const alreadyAdded = useMemo(() => {
        if (!existingRates || !parsedRates) return null;
        return existingRates.filter(r => parsedRates.find(e => r?.attributes?.importUUID === e.attributes?.importUUID));
    }, [existingRates, parsedRates]);

    function showFailed() {
        const win = window.open('', 'Failed Rates', 'toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes');
        win.document.body.innerHTML = '<pre>' + JSON.stringify(failedParsedRates, null, 2) + '</pre>';
    }

    useEffect(() => {
        if (!existingRates || !alreadyAdded) {
            setMessage(null);
            return;
        }
        setMessage(_.compact([
            alreadyAdded.length > 0 && parsedRates.length > alreadyAdded.length ? <span>Found {alreadyAdded.length} {pluralize('rate', alreadyAdded.length)} already added.&nbsp;</span> : null,
            parsedRates.length > alreadyAdded.length ? <span>{parsedRates.length - alreadyAdded.length} {pluralize('rate', parsedRates.length - alreadyAdded.length)} to add.&nbsp;</span> : null,
            parsedRates.length <= alreadyAdded.length ? <span>All rates added.&nbsp;</span> : null,
            failedParsedRates.length > 0 ? <span>Failed to parse {failedParsedRates.length} {pluralize('row', failedParsedRates.length)} from the CSV (click <Button onClick={() => showFailed()} variant='text' sx={{ padding: 0, margin: 0, minWidth: 0, textTransform: 'none' }}>here</Button> to get a their list).&nbsp;</span> : null
        ]));

        setForceSavingSuccess(alreadyAdded.map(r => r.id));
        setTransformedRates(parsedRates.map(r => {
            const existingRate = existingRates.find(e => r.attributes?.importUUID === e.attributes?.importUUID);
            return existingRate || r;
        }));
    }, [parsedRates, existingRates, alreadyAdded, failedParsedRates]);

    async function onLoad() {
        let error = false;

        // Save the new rates
        setMessage(null);
        setLoading('Adding new rates...');

        setForceSaving(transformedRates.map(r => r.id));

        for (const r of transformedRates) {
            const existingRate = existingRates.find(e => r.attributes?.importUUID === e.attributes?.importUUID);
            if (existingRate) {
                setForceSavingSuccess(forceSavingSuccess => [...forceSavingSuccess, r.id]);
                setForceSaving(forceSaving => {
                    setLoading(`Adding new rates (${forceSaving.length - 1} to go)...`);
                    return forceSaving.filter(id => id !== r.id);
                });
                continue;
            }

            try {
                const { data: newRate } = await axios.post(`/api/rates/?accesstoken=${accesstoken}aaa&organizationId=${organization.id}`, { rate: r });
                setForceSavingSuccess(forceSavingSuccess => [...forceSavingSuccess, r.id]);
                setForceSaving(forceSaving => {
                    setLoading(`Adding new rates (${forceSaving.length - 1} to go)...`);
                    return forceSaving.filter(id => id !== r.id);
                });
            } catch (e) {
                console.log('[RatesImport] Error adding rate', e);
                error = true;
                setForceSavingError(forceSavingError => [...forceSavingError, r.id]);
                setForceSaving(forceSaving => {
                    setLoading(`Adding new rates (${forceSaving.length - 1} to go)...`);
                    return forceSaving.filter(id => id !== r.id);
                });
            }
        }

        if (!error) {
            const { data: updatedImport } = await axios.put(`/api/rates/import/${ratesImport.id}/status?organizationId=${organization.id}&accesstoken=${accesstoken}`, { status: 'published' });
            onUpdateRatesImport({ ratesImport: updatedImport });
        }

        setLoading('Retreiving rates to check all is good...');
        const _existingRates = await getRatesFromServer({
            mode: 'rates', accesstoken, organizationId: organization.id, onProgress: ({ count, total }) => {
                if (total > 0) {
                    setLoading(`Retreiving rates to check all is good (${Math.min(Math.round(count / total * 100), 100)}%)...`);
                }
            }
        });
        setLoading(null);
        setExistingRates(_existingRates);
    }

    return (
        <Dialog open={true} onClose={handleClose} maxWidth='xl' fullWidth>
            <DialogTitle>
                {loading ? (
                    <Alert severity='info' sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
                        {loading}
                    </Alert>
                ) : (
                    <></>
                )}
                {message ? (
                    <Alert severity='info' sx={{ mb: 2 }}>
                        {message}
                    </Alert>
                ) : (
                    <></>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant='h6' sx={{ flex: 1 }}>
                        Rates
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Button variant='outlined' color='primary' onClick={() => setForceRelease(true)}>
                            Upload new CSV
                        </Button>
                        <Button variant='contained' color='primary' disabled={loading || !existingRates || !transformedRates || alreadyAdded?.length >= rates?.length} onClick={onLoad}>
                            Add to Rates
                        </Button>
                        <Button onClick={handleClose} color='error' disabled={loading}>
                            <FontAwesomeIcon icon={faXmark} />
                        </Button>
                    </Box>
                </Box>

            </DialogTitle>
            <DialogContent>
                {transformedRates && (
                    <RatesMain
                        mode='rates-import'
                        rates={transformedRates}
                        setRates={null}
                        base={null}
                        setBase={null}
                        products={products}
                        noHeader={true}
                        forceTransportationMethod='sea'
                        forceSaving={forceSaving}
                        forceSavingSuccess={forceSavingSuccess}
                        forceSavingError={forceSavingError}
                        onDeleteOffer={onDeleteOffer}
                    />
                )}
            </DialogContent>
        </Dialog>
    )
}


export { CreateImport, apiCreateRequest, PublishedRatesView };

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    pt: 2,
    px: 4,
    pb: 3,
};

async function deterministicUUID(fileName, index) {
    const input = `${fileName}:${index}`;

    // Create a hash using SHA-1
    const buffer = new TextEncoder().encode(input);
    return crypto.subtle.digest('SHA-1', buffer).then(hashBuffer => {
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

        // Format as UUID v5 style (version 5 is also SHA-1 based)
        return (
            hex.slice(0, 8) + '-' +
            hex.slice(8, 12) + '-' +
            '5' + hex.slice(13, 16) + '-' + // version 5
            ((parseInt(hex.slice(16, 18), 16) & 0x3f | 0x80).toString(16)) + hex.slice(18, 20) + '-' +
            hex.slice(20, 32)
        );
    });
}